import React, { memo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { parse, format, isValid } from 'date-fns';
import type { DoctorShift } from '../../../../../types/doctorShift';

/**
 * ShiftEditPopup Props
 */
interface ShiftEditPopupProps {
  /** ポップアップ表示状態 */
  open: boolean;
  /** 編集対象のシフト（新規作成時は空のシフトオブジェクト） */
  shift: DoctorShift | null;
  /** 新規作成モード */
  isCreating: boolean;
  /** 読み込み中状態 */
  isLoading: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** 保存ハンドラー */
  onSave: (data: Partial<DoctorShift>) => Promise<boolean>;
  /** 削除ハンドラー */
  onDelete: (id: string, version: number) => Promise<boolean>;
  /** キャンセルハンドラー */
  onCancel: () => void;
}

/**
 * フォーム状態型
 */
interface FormState {
  doctor_id: string;
  shift_date: Date | null;
  start_time: Date | null;
  end_time: Date | null;
  slot_type: 'normal' | 'urgent';
}

/**
 * 時間文字列をDateオブジェクトに変換
 */
const timeStringToDate = (timeStr: string): Date | null => {
  if (!timeStr) return null;
  const parsed = parse(timeStr, 'HH:mm', new Date());
  return isValid(parsed) ? parsed : null;
};

/**
 * DateオブジェクトをHH:mm形式の文字列に変換
 */
const dateToTimeString = (date: Date | null): string => {
  if (!date || !isValid(date)) return '';
  return format(date, 'HH:mm');
};

/**
 * シフト編集ポップアップコンポーネント
 *
 * @description
 * シフトの新規作成・編集・削除を行うポップアップダイアログ。
 * 医師ID、日付、開始/終了時間、応募枠種別を編集可能。
 *
 * @param {ShiftEditPopupProps} props - コンポーネントProps
 * @returns {React.ReactElement} シフト編集ポップアップ
 */
const ShiftEditPopup: React.FC<ShiftEditPopupProps> = memo(
  ({ open, shift, isCreating, isLoading, error, onSave, onDelete, onCancel }) => {
    const [form, setForm] = useState<FormState>({
      doctor_id: '',
      shift_date: null,
      start_time: null,
      end_time: null,
      slot_type: 'normal',
    });

    // シフトデータが変更されたらフォームを更新
    useEffect(() => {
      if (shift) {
        setForm({
          doctor_id: shift.doctor_id || '',
          shift_date: shift.shift_date ? new Date(shift.shift_date) : null,
          start_time: timeStringToDate(shift.start_time),
          end_time: timeStringToDate(shift.end_time),
          slot_type: shift.slot_type || 'normal',
        });
      }
    }, [shift]);

    /**
     * テキストフィールド変更ハンドラー
     */
    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setForm((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * Select変更ハンドラー
     */
    const handleSelectChange = (event: SelectChangeEvent<'normal' | 'urgent'>) => {
      setForm((prev) => ({ ...prev, slot_type: event.target.value as 'normal' | 'urgent' }));
    };

    /**
     * 日付変更ハンドラー
     */
    const handleDateChange = (date: Date | null) => {
      setForm((prev) => ({ ...prev, shift_date: date }));
    };

    /**
     * 開始時間変更ハンドラー
     */
    const handleStartTimeChange = (time: Date | null) => {
      setForm((prev) => ({ ...prev, start_time: time }));
    };

    /**
     * 終了時間変更ハンドラー
     */
    const handleEndTimeChange = (time: Date | null) => {
      setForm((prev) => ({ ...prev, end_time: time }));
    };

    /**
     * 保存ハンドラー
     */
    const handleSave = async () => {
      const data: Partial<DoctorShift> = {
        doctor_id: form.doctor_id,
        shift_date: form.shift_date ? format(form.shift_date, 'yyyy-MM-dd') : '',
        start_time: dateToTimeString(form.start_time),
        end_time: dateToTimeString(form.end_time),
        slot_type: form.slot_type,
      };
      await onSave(data);
    };

    /**
     * 削除ハンドラー
     */
    const handleDelete = async () => {
      if (shift && shift.id) {
        await onDelete(shift.id, shift.version || 0);
      }
    };

    return (
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreating ? 'シフト新規作成' : 'シフト編集'}</DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* 医師名 */}
            <TextField
              name="doctor_name"
              label="医師名"
              value={shift?.doctor_name || shift?.doctor_full_name || ''}
              fullWidth
              disabled
              InputProps={{
                readOnly: true,
              }}
            />

            {/* 日付 */}
            <DatePicker
              label="日付"
              value={form.shift_date}
              onChange={handleDateChange}
              disabled={isLoading}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
              }}
              format="yyyy/MM/dd"
            />

            {/* 開始時間 */}
            <TimePicker
              label="開始時間"
              value={form.start_time}
              onChange={handleStartTimeChange}
              disabled={isLoading}
              ampm={false}
              minutesStep={15}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
              }}
            />

            {/* 終了時間 */}
            <TimePicker
              label="終了時間"
              value={form.end_time}
              onChange={handleEndTimeChange}
              disabled={isLoading}
              ampm={false}
              minutesStep={15}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
              }}
            />

            {/* 枠種別 */}
            <FormControl fullWidth>
              <InputLabel>枠種別</InputLabel>
              <Select
                value={form.slot_type}
                onChange={handleSelectChange}
                label="枠種別"
                disabled={isLoading}
              >
                <MenuItem value="normal">通常枠</MenuItem>
                <MenuItem value="urgent">急募枠</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!isCreating && shift?.id && (
            <Button
              onClick={handleDelete}
              color="error"
              disabled={isLoading}
              sx={{ mr: 'auto' }}
            >
              削除
            </Button>
          )}

          <Button onClick={onCancel} disabled={isLoading}>
            キャンセル
          </Button>

          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

ShiftEditPopup.displayName = 'ShiftEditPopup';

export { ShiftEditPopup };
